/**
 * Authentication state management with Zustand
 * 
 * Handles login, logout, token refresh, and user state
 */

import { create } from 'zustand';
import { tokenStorage } from './token-storage';
import { AuthUser, LoginResponse, SmartFieldClaimType, UserInfo } from './types';
import { accountLoginToken, accountGetInfo, accountLogout } from '../../api';
import { OpenAPI } from '../../api/core/OpenAPI';

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshTokens: () => Promise<void>;
  loadUser: () => Promise<void>;
  clearError: () => void;
  initialize: () => Promise<void>;
}

/**
 * Parse user claims into structured format
 */
function parseUserInfo(userInfo: UserInfo): AuthUser {
  const claimsMap = new Map<string, string[]>();
  const roles: string[] = [];
  const sitePermissions = new Map<SmartFieldClaimType, number[]>();
  const affiliatedSiteIdsSet = new Set<number>();

  // Handle case where claims might not be present or not be an array
  if (userInfo.claims && Array.isArray(userInfo.claims)) {
    for (const claim of userInfo.claims) {
      const existing = claimsMap.get(claim.type) || [];
      claimsMap.set(claim.type, [...existing, claim.value]);

      // Extract roles
      if (claim.type === 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role') {
        roles.push(claim.value);
      }

      // Parse SmartFieldClaimType claims with site IDs
      if (Object.values(SmartFieldClaimType).includes(claim.type as SmartFieldClaimType)) {
        const claimType = claim.type as SmartFieldClaimType;
        const siteId = parseInt(claim.value, 10);
        
        if (!isNaN(siteId)) {
          const existing = sitePermissions.get(claimType) || [];
          sitePermissions.set(claimType, [...existing, siteId]);
          affiliatedSiteIdsSet.add(siteId);
        }
      }
    }
  }

  return {
    email: userInfo.email,
    isEmailConfirmed: userInfo.isEmailConfirmed,
    claims: claimsMap,
    roles,
    sitePermissions,
    affiliatedSiteIds: Array.from(affiliatedSiteIdsSet),
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,

  initialize: async () => {
    try {
      set({ isLoading: true });

      // Try to restore tokens from sessionStorage
      const tokens = tokenStorage.initialize();

      if (tokens && tokens.accessToken) {
        try {
          await get().loadUser();
        } catch {
          // Access token may be expired — try refresh
          await get().refreshTokens();
        }
      } else if (tokenStorage.getRefreshToken()) {
        // No access token in session but refresh token exists — try refresh
        await get().refreshTokens();
      } else {
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      tokenStorage.clear();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: 'Failed to restore session'
      });
    }
  },

  login: async (email: string, password: string, rememberMe = false) => {
    try {
      set({ isLoading: true, error: null });

      const response = await accountLoginToken({
        requestBody: { email, password }
      }) as LoginResponse;

      // Store tokens
      tokenStorage.setTokens(
        response.accessToken,
        response.refreshToken,
        response.expiresIn,
        rememberMe
      );

      // Load user info
      await get().loadUser();

      set({ isLoading: false });
    } catch (error) {
      console.error('Login failed:', error);
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Login failed'
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      // Call logout endpoint (best effort, don't wait for response)
      accountLogout().catch(() => {
        // Ignore errors - logout locally anyway
      });
    } finally {
      tokenStorage.clear();
      set({ 
        user: null, 
        isAuthenticated: false, 
        error: null 
      });
    }
  },

  refreshTokens: async () => {
    const refreshToken = tokenStorage.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      // Call the refresh endpoint directly instead of the generated client:
      // the generated call routes through OpenAPI.TOKEN -> ensureValidToken(),
      // which would re-enter and deadlock on the in-flight refresh (the /refresh
      // endpoint doesn't require a bearer token anyway).
      const refreshResponse = await fetch(`${OpenAPI.BASE}/v1/account/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!refreshResponse.ok) {
        throw new Error(`Refresh failed with status ${refreshResponse.status}`);
      }

      const response = await refreshResponse.json() as LoginResponse;

      tokenStorage.setTokens(
        response.accessToken,
        response.refreshToken,
        response.expiresIn
      );

      // Optionally reload user info to get fresh claims
      if (!get().user) {
        await get().loadUser();
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear auth state on refresh failure
      get().logout();
      throw error;
    }
  },

  loadUser: async () => {
    try {
      const userInfo = await accountGetInfo() as UserInfo;
      const user = parseUserInfo(userInfo);
      
      set({ 
        user, 
        isAuthenticated: true,
        isLoading: false,
        error: null
      });
    } catch (error) {
      console.error('Failed to load user info:', error);
      tokenStorage.clear();
      set({ 
        user: null, 
        isAuthenticated: false,
        isLoading: false,
        error: 'Failed to load user information'
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
